import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { MessageService } from 'primeng/api';
import { bmxurl, testToolsAllowed } from 'src/globals';
import { GlobalService } from '../_services/global.service';
import { BusyService } from '../_services/busy.service';
import { resource } from 'src/types/resource';
import { checkAll, CopyRow, EmptyContainer, findAllEmptyContainers, Holder, loadBothLists } from './copyfinder';

/*
  Package Test - Resource Test's partner for packages, for the same one login.

  A package keeps its own copies of resources, in more than one place: the package resources and
  their resources, additionalresources, extras, upgrades. Every resource is checked in turn against
  every package (events included, as they are packages too), and the copies whose concurrentUses
  has drifted are listed. Containers inside a package with no resources are listed too, as they
  break the booking process. See copyfinder.ts for how.
*/
@Component({
  selector: 'app-packagetest',
  templateUrl: './packagetest.component.html',
  styleUrls: ['./resourcetest.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PackageTestComponent implements OnInit {
  //Only for the login in testToolsEmail - known once Auth0 has said who is logged in
  available: boolean = false;
  checked: boolean = false;

  VenueID: string = localStorage.getItem("SmartVenueID")!;

  resources: resource[] = [];
  packages: Holder[] = [];
  failedLoads: string[] = [];
  loaded: boolean = false;
  rows: CopyRow[] = [];
  empty: EmptyContainer[] = [];

  constructor(private http: HttpClient, private messageService: MessageService, private globalService: GlobalService, private busyService: BusyService, private auth: AuthService) { }

  ngOnInit(): void {
    this.globalService.setSmallScreenMenu(false);

    this.auth.user$.pipe(take(1)).subscribe(user => {
      this.available = testToolsAllowed(user?.email);
      this.checked = true;

      if (this.available)
        this.load();
    });
  }

  load() {
    this.busyService.busy();

    forkJoin({
      resources: loadBothLists(this.http, bmxurl + "ResourceList/" + this.VenueID + "//", 'resourceId'),
      packages: loadBothLists(this.http, bmxurl + "packagelist/" + this.VenueID + "/", 'packageId')
    }).subscribe(lists => {
      if (lists.resources == null || lists.packages == null) {
        this.busyService.idle();
        this.messageService.add({severity:'error', summary:'Not Loaded', detail:'The resource or package list could not be loaded.'});
        return;
      }

      this.resources = [...lists.resources].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

      //The list does not carry a package's resources in full, so fetch each one. Disabled packages
      //are left out - they cannot be booked, so a stale copy in one does no harm.
      const fetches: Observable<Holder | null>[] = lists.packages.filter(p => !p.disabled).map(p => this.fetchPackage(p));

      (fetches.length == 0 ? of([]) : forkJoin(fetches)).subscribe(packages => {
        this.packages = (packages.filter(p => p != null) as Holder[])
          .sort((a, b) => a.kind.localeCompare(b.kind) || (a.name ?? '').localeCompare(b.name ?? ''));
        this.rows = checkAll(this.resources, this.packages);
        this.empty = findAllEmptyContainers(this.packages);
        this.loaded = true;
        this.busyService.idle();
      });
    });
  }

  //A failed fetch is noted and skipped, so one bad package does not stop the rest being checked
  fetchPackage(listed: any): Observable<Holder | null> {
    return this.http.get<any>(bmxurl + "package/" + this.VenueID + "/" + listed.packageId).pipe(
      map(response => {
        if (!response?.succeeded || response.data == null) {
          this.failedLoads.push(listed.name);
          return null;
        }
        return { kind: listed.eventid != null ? 'Event' : 'Package', name: listed.name, disabled: listed.disabled, data: response.data } as Holder;
      }),
      catchError(() => {
        this.failedLoads.push(listed.name);
        return of(null);
      })
    );
  }
}
